<template>
	<v-card>
		<v-card-title>
			<div>{{ buttonLabel }}</div>
			<v-icon right class="ml-auto theme--dark" @click="$emit('cancel')">mdi-close</v-icon>
		</v-card-title>
		<v-divider class="mb-5" />
		<v-card-text>
			<v-form v-model="formValid" class="pt-2" @submit.prevent="handleForm">
				<v-row>
					<v-col>
						<v-text-field
							v-model="form.first_name"
							label="First Name"
							name="first_name"
							:hide-details="! hasErrors(form, 'first_name', 'updateProfileInformation')"
							:error="hasErrors(form, 'first_name', 'updateProfileInformation')"
							:error-messages="errorMsg(form, 'first_name', 'updateProfileInformation')"
							persistent-placeholder
							autofocus
							outlined
							required
							dense />
					</v-col>
					<v-col>
						<v-text-field
							v-model="form.last_name"
							label="Last Name"
							name="last_name"
							:hide-details="! hasErrors(form, 'last_name', 'updateProfileInformation')"
							:error="hasErrors(form, 'last_name', 'updateProfileInformation')"
							:error-messages="errorMsg(form, 'last_name', 'updateProfileInformation')"
							persistent-placeholder
							outlined
							required
							dense />
					</v-col>
				</v-row>
				<v-row>
					<v-col>
						<v-text-field
							v-model="form.email"
							name="email"
							type="email"
							label="Email Address"
							:error="hasErrors(form, 'email', 'updateProfileInformation')"
							:error-messages="errorMsg(form, 'email', 'updateProfileInformation')"
							persistent-placeholder
							outlined
							required
							dense />

						<v-text-field
							v-model="form.phone"
							name="phone"
							label="Phone"
							:error="hasErrors(form, 'phone', 'updateProfileInformation')"
							:error-messages="errorMsg(form, 'phone', 'updateProfileInformation')"
							persistent-placeholder
							outlined
							required
							dense />

						<v-autocomplete
							v-if="auth.user_type === 1"
							v-model="form.client_id"
							:items="clients"
							:loading="! length(clients)"
							:error="hasErrors(form, 'client_id')"
							:hide-details="! hasErrors(form, 'client_id')"
							:error-messages="errorMsg(form, 'client_id')"
							persistent-placeholder
							label="Select Client"
							placeholder="Type to search clients"
							item-text="name"
							item-value="id"
							filled
							eager
							dense
							outlined
							clearable
							required>
						</v-autocomplete>

						<v-select
							v-if="auth.user_type === 1"
							v-model="form.user_type"
							:items="types"
							:hide-details="! hasErrors(form, 'user_type', 'updateProfileInformation')"
							:error="hasErrors(form, 'user_type', 'updateProfileInformation')"
							:error-messages="errorMsg(form, 'user_type', 'updateProfileInformation')"
							persistent-placeholder
							item-text="name"
							item-value="id"
							label="Select User Type"
							outlined
							class="mt-7"
							dense />

						<v-row v-if="user" class="mt-4">
							<v-col>
								<v-text-field
									v-model="form.password"
									type="password"
									label="Password"
									hint="Leave blank for no change."
									persistent-hint
									:error="hasErrors(form, 'password', 'updatePassword')"
									:error-messages="errorMsg(form, 'password', 'updatePassword')"
									persistent-placeholder
									outlined
									required
									dense />
							</v-col>
							<v-col>
								<v-text-field
									v-model="form.password_confirmation"
									type="password"
									label="Password (again)"
									:disabled="!form.password"
									:error="hasErrors(form, 'password_confirmation', 'updatePassword')"
									:error-messages="errorMsg(form, 'password_confirmation', 'updatePassword')"
									persistent-placeholder
									outlined
									:required="form.password"
									dense />
							</v-col>
						</v-row>

						<v-card v-if="! user" outlined class="mt-4">
							<v-card-text>
								<v-switch
									v-model="form.invite"
									hint="Will email this user a welcome email with a randomly generated password."
									persistent-hint
									class="mt-0"
									label="Send Invite?"></v-switch>
							</v-card-text>
						</v-card>

						<v-row v-if="user && user.id !== auth.id">
							<v-col>
								<v-card outlined class="mt-2">
									<v-card-text>
										<v-switch
											v-model="form.invite"
											hint="Will email this user a welcome email with their new password."
											persistent-hint
											class="mt-0"
											label="Re-send Invite"></v-switch>
									</v-card-text>
								</v-card>
							</v-col>
							<v-col>
								<v-card outlined class="mt-2">
									<v-card-text>
										<v-switch
											v-model="form.is_active"
											hint="Inactive users cannot login."
											persistent-hint
											class="mt-0"
											label="Active User"></v-switch>
									</v-card-text>
								</v-card>
							</v-col>
						</v-row>
					</v-col>
				</v-row>
			</v-form>
		</v-card-text>
		<v-divider />
		<v-card-actions>
			<v-btn
				color="success"
				large
				:loading="form.processing"
				@click="handleForm">
				{{ buttonLabel }}
			</v-btn>
		</v-card-actions>
	</v-card>
</template>
<script>
import api from '@/api'

export default {
	props: {
		user: {
			type: Object,
			default: () => {}
		}
	},
	data () {
		return {
			auth: this.$page.props.auth,
			types: [],
			clients: [],
			formValid: true,
			form: this.$inertia.form({
				first_name: null,
				last_name: null,
				invite: true,
				phone: null,
				email: null,
				user_type: null,
				client_id: null,
				is_active: false,
				password: null,
				password_confirmation: null
			})
		}
	},
	computed: {
		buttonLabel () {
			if (this.user) {
				return 'Update User'
			}

			return 'Create User'
		}
	},
	watch: {
		user () {
			this.populateForm()
		},
		'form.password' () {
			this.form.invite = true
			this.form.is_active = true
		}
	},
	mounted () {
		this.populateForm()
		this.getUserTypes()
		this.getClients()
	},
	methods: {
		populateForm () {
			if (this.user) {
				for (const userKey in this.user) {
					if (!this.user.hasOwnProperty(userKey)) continue
					if (this.form.hasOwnProperty(userKey)) {
						this.$set(this.form, userKey, this.user[userKey])
					}
				}

				this.form.invite = false
			}
		},

		getUserTypes () {
			const method = 'GET'
			const data = {}

			const url = this.route('users.api.types')

			return api({
				method,
				url,
				data
			}).then(response => {
				this.types = response.data
			})
		},

		handleForm () {
			if (this.user) {
				this.form.put(this.route('users.update', this.user), {
					onSuccess: () => this.$emit('success')
				})

				return
			}

			this.form.post(this.route('users.store'), {
				only: ['errors', 'flash'],
				onSuccess: () => this.$emit('success')
			})
		}
	}
}
</script>
